/**
 * PHEV Savings Calculation Module
 *
 * Pure, unit-testable logic behind the PhevCalculator and SavingsDetails
 * components. Each value that influences the annual savings figure is an
 * explicit input, so the behaviour can be validated in isolation.
 *
 * Model (per day, see documentation/readme.md):
 *   Efficiency conversion : kWh/100km = PHEV_Electric_Efficiency (L/100km Le) * 8.9
 *   Electric_KM    = MIN(Daily_KM, PHEV_Electric_Range)
 *   Hybrid_KM      = MAX(0, Daily_KM - Electric_KM)
 *   Hybrid_Cost    = (Hybrid_KM / 100) * PHEV_Hybrid_Economy * Gas_Cost
 *   Electric_Cost  = (Electric_KM / 100) * (PHEV_Electric_Efficiency * 8.9) * Electricity_Cost
 *   ICE_Cost       = (Daily_KM / 100) * ICE_Fuel_Economy * Gas_Cost
 *
 * Monthly average = (Weekday_Daily_Cost * 21.67) + (Weekend_Daily_Cost * 8.67).
 * Yearly total    = Monthly average * 12.
 * Savings         = ICE cost - PHEV cost, floored at 0, projected over N years.
 */

/**
 * NRCan conversion factor: 1 L of gasoline contains the energy equivalent of
 * 8.9 kWh of electricity. Used to turn an Le rating (L/100km) into kWh/100km.
 */
export const LE_TO_KWH_FACTOR = 8.9;

/** Weekday days in an average month, used for monthly extrapolation. */
export const WEEKDAY_DAYS_PER_MONTH = 21.67;

/** Weekend days in an average month, used for monthly extrapolation. */
export const WEEKEND_DAYS_PER_MONTH = 8.67;

/**
 * Mandatory unit conversion. Convert a fuel-consumption (Le) rating expressed
 * in L/100km into an electrical efficiency expressed in kWh/100km.
 */
export function convertLeToKwh(leRating: number): number {
  return leRating * LE_TO_KWH_FACTOR;
}

export interface VehicleSpec {
  name: string;
  nameFr?: string;
  phevElectricRange: number;
  phevHybridEconomy: number;
  /** Le rating (L/100km); converted to kWh/100km via * 8.9. */
  phevElectricEfficiency: number;
}

/**
 * Returns the vehicle name in the active language, falling back to the
 * English name when no French name is available.
 */
export function getLocalizedVehicleName(vehicle: VehicleSpec, language: string): string {
  if (language === 'fr') {
    return vehicle.nameFr ?? vehicle.name;
  }
  return vehicle.name;
}

/** PHEV-side figures shown in the "How we calculated" breakdown. */
export interface PhevBreakdown {
  totalAnnualKM: number;
  yearlyElectricKM: number;
  yearlyHybridKM: number;
  electricityYearlyCost: number;
  fuelYearlyCost: number;
  phevYearlyCost: number;
}

/** Final savings figures shown in the sidebar. */
export interface SavingsResult {
  iceYearlyCost: number;
  phevYearlyCost: number;
  yearlySaving: number;
  monthlySaving: number;
  projectedSaving: number;
}

/**
 * Distance covered on electric power for a single day.
 * Electric_KM = MIN(Daily_KM, PHEV_Electric_Range)
 */
export function calcElectricKM(dailyKM: number, phevElectricRange: number): number {
  return Math.min(dailyKM, phevElectricRange);
}

/**
 * Distance covered on hybrid power for a single day.
 * Hybrid_KM = MAX(0, Daily_KM - Electric_KM)
 */
export function calcHybridKM(dailyKM: number, electricKM: number): number {
  return Math.max(0, dailyKM - electricKM);
}

/** Hybrid_Cost = (Hybrid_KM / 100) * PHEV_Hybrid_Economy * Fuel_Cost */
export function calcHybridCost(
  hybridKM: number,
  phevHybridEconomy: number,
  fuelCost: number
): number {
  return (hybridKM / 100) * phevHybridEconomy * fuelCost;
}

/**
 * Electric_Cost = (Electric_KM / 100) * (PHEV_Electric_Efficiency * 8.9) * Electricity_Cost
 * The Le rating (L/100km) is converted to kWh/100km via the 8.9 factor.
 */
export function calcElectricCost(
  electricKM: number,
  phevElectricEfficiency: number,
  electricityCost: number
): number {
  return (electricKM / 100) * convertLeToKwh(phevElectricEfficiency) * electricityCost;
}

/**
 * Total daily cost to run the PHEV (hybrid + electric portions).
 */
export function calcPhevDailyCost(
  dailyKM: number,
  vehicle: VehicleSpec,
  fuelCost: number,
  electricityCost: number
): number {
  const electricKM = calcElectricKM(dailyKM, vehicle.phevElectricRange);
  const hybridKM = calcHybridKM(dailyKM, electricKM);
  return (
    calcHybridCost(hybridKM, vehicle.phevHybridEconomy, fuelCost) +
    calcElectricCost(electricKM, vehicle.phevElectricEfficiency, electricityCost)
  );
}

/**
 * Total daily cost of the current ICE vehicle.
 * ICE_Cost = (Daily_KM / 100) * ICE_Fuel_Economy * Fuel_Cost
 */
export function calcIceDailyCost(
  dailyKM: number,
  iceFuelEconomy: number,
  fuelCost: number
): number {
  return (dailyKM / 100) * iceFuelEconomy * fuelCost;
}

/**
 * Monthly average cost for a weekday/weekend pattern.
 * Monthly = (Weekday_Daily_Cost * 21.67) + (Weekend_Daily_Cost * 8.67)
 */
export function calcMonthlyCost(weekdayCost: number, weekendCost: number): number {
  return weekdayCost * WEEKDAY_DAYS_PER_MONTH + weekendCost * WEEKEND_DAYS_PER_MONTH;
}

/**
 * Yearly total = 12 x the average monthly cost.
 */
export function calcYearlyCost(weekdayCost: number, weekendCost: number): number {
  return calcMonthlyCost(weekdayCost, weekendCost) * 12;
}

/**
 * Full PHEV-side breakdown for a year, mirroring the grid in SavingsDetails.
 */
export function calculatePhevBreakdown(
  weekdayKM: number,
  weekendKM: number,
  vehicle: VehicleSpec,
  phevFuelCost: number,
  electricityCost: number
): PhevBreakdown {
  const eKMWeekday = calcElectricKM(weekdayKM, vehicle.phevElectricRange);
  const eKMWeekend = calcElectricKM(weekendKM, vehicle.phevElectricRange);
  const hybridKMWeekday = calcHybridKM(weekdayKM, eKMWeekday);
  const hybridKMWeekend = calcHybridKM(weekendKM, eKMWeekend);

  const electricityYearlyCost = calcYearlyCost(
    calcElectricCost(eKMWeekday, vehicle.phevElectricEfficiency, electricityCost),
    calcElectricCost(eKMWeekend, vehicle.phevElectricEfficiency, electricityCost)
  );
  const fuelYearlyCost = calcYearlyCost(
    calcHybridCost(hybridKMWeekday, vehicle.phevHybridEconomy, phevFuelCost),
    calcHybridCost(hybridKMWeekend, vehicle.phevHybridEconomy, phevFuelCost)
  );

  return {
    totalAnnualKM: calcYearlyCost(weekdayKM, weekendKM),
    yearlyElectricKM: calcYearlyCost(eKMWeekday, eKMWeekend),
    yearlyHybridKM: calcYearlyCost(hybridKMWeekday, hybridKMWeekend),
    electricityYearlyCost,
    fuelYearlyCost,
    phevYearlyCost: electricityYearlyCost + fuelYearlyCost,
  };
}

/**
 * Computes every savings figure displayed by the calculator.
 * Savings are floored at 0 (a PHEV can't "cost you more" on the breakdown,
 * it just shows zero savings).
 */
export function calculateSavings(
  weekdayKM: number,
  weekendKM: number,
  vehicle: VehicleSpec,
  iceFuelEconomy: number,
  phevFuelCost: number,
  electricityCost: number,
  projectionYears: number
): SavingsResult {
  const iceYearlyCost = calcYearlyCost(
    calcIceDailyCost(weekdayKM, iceFuelEconomy, phevFuelCost),
    calcIceDailyCost(weekendKM, iceFuelEconomy, phevFuelCost)
  );
  const phevYearlyCost = calculatePhevBreakdown(
    weekdayKM,
    weekendKM,
    vehicle,
    phevFuelCost,
    electricityCost
  ).phevYearlyCost;

  const yearlySaving = Math.max(0, iceYearlyCost - phevYearlyCost);
  const monthlySaving = yearlySaving / 12;
  const projectedSaving = yearlySaving * projectionYears;

  return {
    iceYearlyCost,
    phevYearlyCost,
    yearlySaving,
    monthlySaving,
    projectedSaving,
  };
}
